import { AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { breachedResults, describeLimit, formatWeatherValue, sourceLabel } from '../../utils/sprayWeatherLimits';
import type { WeatherLimitCheck } from '../../types/spray';

interface WeatherLimitAckDialogProps {
  check: WeatherLimitCheck;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The server refused Start with WEATHER_LIMITS_BREACHED. The operator is the
 * one in the paddock and may know the block is sheltered, so this can be
 * passed — but only by someone who has seen exactly which limits and by how
 * much. Starting anyway is recorded against their name and the creator is
 * told.
 */
function WeatherLimitAckDialog({ check, busy = false, onConfirm, onCancel }: WeatherLimitAckDialogProps) {
  const timezone = useOrgTimezone();
  const breaches = breachedResults(check.results);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="wx-ack-title" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
          <h3 id="wx-ack-title" className="text-base font-semibold text-neutral-800">Weather is outside this spray’s limits</h3>
        </div>

        <ul className="mb-4 space-y-2">
          {breaches.map(r => {
            const src = sourceLabel(r.source);
            return (
              <li key={`${r.metric}:${r.bound}`} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-sm font-medium text-red-800">
                  {r.label}: {formatWeatherValue(r.value, r.unit)}
                </p>
                <p className="text-xs text-red-700">
                  Limit {describeLimit(r)}
                  {src && <> · {src}</>}
                  {r.forecast_at && <> · {formatDateTimeInTz(r.forecast_at, timezone)}</>}
                </p>
              </li>
            );
          })}
        </ul>

        <p className="mb-5 text-sm text-neutral-600">
          Starting anyway is recorded against your name, and whoever created this spray is notified.
        </p>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>Don’t start</Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Starting…' : 'Start anyway'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default WeatherLimitAckDialog;
